'use client';

import { useYearLowestPriceBadgeEnabled } from '../../YearLowestPriceBadgeContext';
import Image from '@/components/Image';
import priceDrop from './assets/price-drop.svg';
import priceDropPc from './assets/price-drop-pc.svg';
import priceDropDetailMobile from './assets/price-drop-detail-mobile.svg';

const labelSizes = {
  mobile: { className: 'text-12', icon: priceDrop, width: 12, height: 12 },
  desktop: { className: 'min-h-[20px] text-11', icon: priceDropPc, width: 14, height: 14 },
  'detail-mobile': {
    className: 'min-h-[18px] text-12',
    icon: priceDropDetailMobile,
    width: 12,
    height: 12,
  },
  'detail-desktop': {
    className: 'min-h-[20px] text-14',
    icon: priceDropPc,
    width: 14,
    height: 14,
  },
} as const;

const badgeSizes = {
  'card-desktop': 'h-[24px] max-w-full rounded-[24px] px-[10px] text-[12px] leading-[14px]',
  'card-mobile': 'h-[18px] max-w-full rounded-[18px] px-[6px] text-[11px] leading-[normal]',
  pc: 'absolute left-[20px] top-[20px] max-w-[calc(100%-72px)] rounded-[100px] px-[10px] py-[5px] text-[12px] leading-[14px]',
  'wap-primary':
    'absolute left-[12px] top-[12px] h-[20px] max-w-[calc(100%-52px)] rounded-[24px] px-[6px] text-[11px] leading-[normal]',
  'wap-secondary':
    'absolute left-[8px] top-[8px] h-[20px] max-w-[calc(100%-44px)] rounded-[24px] px-[6px] text-[11px] leading-[normal]',
  'wap-compact':
    'absolute left-[8px] top-[8px] h-[16px] max-w-[calc(100%-16px)] rounded-[24px] px-[4px] text-[10px] leading-[12px]',
} as const;

type YearLowestPriceLabelProps = { text?: string } & (
  | { variant?: 'inline'; size?: keyof typeof labelSizes }
  | { variant: 'badge'; size: keyof typeof badgeSizes }
);

export default function YearLowestPriceLabel(props: YearLowestPriceLabelProps) {
  const enabled = useYearLowestPriceBadgeEnabled();
  const { text } = props;
  const label = typeof text === 'string' ? text.trim() : '';
  if (!label || (props.variant === 'badge' && !enabled)) return null;

  if (props.variant === 'badge') {
    return (
      <span
        data-year-lowest-price
        data-year-lowest-price-variant="badge"
        className={`pointer-events-none z-[3] inline-flex min-w-0 items-center overflow-hidden bg-gradient-to-r from-promotion-red to-black font-br font-normal text-white ${badgeSizes[props.size]}`}
        title={label}
      >
        <span className="truncate">{label}</span>
      </span>
    );
  }

  const labelSize = labelSizes[props.size ?? 'mobile'];

  return (
    <span
      data-year-lowest-price
      className={`inline-flex max-w-full min-w-0 items-center gap-4 font-bs text-promotion-red ${labelSize.className}`}
    >
      <Image
        src={labelSize.icon}
        alt=""
        width={labelSize.width}
        height={labelSize.height}
        className="shrink-0"
      />
      <span className="min-w-0 break-words">{label}</span>
    </span>
  );
}
